import { useEffect, useState } from "react";
import { Icon, RedirectToOUI, Common } from "@/components";
import { addressGenerate, SnackbarUtils } from "@/utils";
import { useAppSelector, useAppDispatch } from "@/sharedStore/hooks";
import {
  storageAddressSelector,
  storageAddressLoadedSelector,
  storageAddressLoadingSelector,
} from "../providers/deliveryManifest/selector";
import { storageAddress } from "../models/DeliveryManifest";
import LocateAddressDialog from "@/components/Googlemap/LocateAddressDialog";
import { saveStorageAddress } from "../providers/deliveryManifest/sliceServices";
import { LocationWithComponents } from "@/utils/address";

export const StorageAddress = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dispatch = useAppDispatch();

  const storageAddress = useAppSelector(
    storageAddressSelector
  ) as storageAddress;
  const isLoaded = useAppSelector(storageAddressLoadedSelector);
  const isLoading = useAppSelector(storageAddressLoadingSelector);
  const subscribedProductType = useAppSelector(
    (state) => state.authReducer.user?.subscribedProductType
  );

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmAddress = (location: LocationWithComponents) => {
    if (!location.lat || !location.lng) {
      SnackbarUtils.error("We could not locate the address on the map");
      return;
    }

    const addressData: Omit<storageAddress, "name"> = {
      addressLine1: location.components?.addressLine1,
      addressLine2: location.components?.addressLine2 || "",
      suburb: location.components?.suburb,
      state: location.components?.state,
      postcode: location.components?.postcode,
      country: location.components?.country,
      latitude: location.lat ?? 0,
      longitude: location.lng ?? 0,
    };

    dispatch(saveStorageAddress(addressData as storageAddress));
    handleCloseDialog();
  };

  useEffect(() => {
    if (!storageAddress) {
      setIsDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex-1 flex items-center text-neutral-60 px-4">
      <div>
        <Icon.Shop className="fill-neutral-60 mr-2" />{" "}
      </div>
      {isLoaded ? (
        <>
          {!storageAddress && (
            <>
              {subscribedProductType === 1 ? (
                <Common.Button
                  color="blue"
                  onClick={handleOpenDialog}
                  className="ml-2"
                >
                  Add depot address
                </Common.Button>
              ) : (
                <RedirectToOUI
                  to={"/delivery/settings#tab2"}
                  className="ml-2 font-semibold cursor-pointer text-blue hover:underline"
                >
                  Add depot address.
                </RedirectToOUI>
              )}
            </>
          )}
          {storageAddress && (
            <>
              <span className="font-semibold whitespace-nowrap mr-2 hidden 2xl:inline-block">
                Depot address:{" "}
              </span>
              <span className="line-clamp-1 min-w-[10rem]">
                {addressGenerate(
                  storageAddress?.addressLine1,
                  storageAddress?.addressLine2,
                  storageAddress?.suburb,
                  storageAddress?.state,
                  storageAddress?.postcode,
                  storageAddress?.country
                )}
              </span>
              {subscribedProductType === 1 ? (
                <Common.Button
                  variant="link"
                  color="blue"
                  onClick={handleOpenDialog}
                  className="font-semibold cursor-pointer text-blue hover:underline p-0"
                >
                  change
                </Common.Button>
              ) : (
                <RedirectToOUI
                  to={"/delivery/settings#tab2"}
                  className="ml-2 font-semibold cursor-pointer text-blue hover:underline"
                >
                  change
                </RedirectToOUI>
              )}
            </>
          )}

          <LocateAddressDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            loading={isLoading}
            title="Setup depot address"
            description={
              !storageAddress
                ? "You have not setup a depot address yet. Please set it up before continuing."
                : undefined
            }
            currentAddress={
              storageAddress
                ? addressGenerate(
                    storageAddress?.addressLine1,
                    storageAddress?.addressLine2,
                    storageAddress?.suburb,
                    storageAddress?.state,
                    storageAddress?.postcode,
                    storageAddress?.country
                  )
                : ""
            }
            initialLocation={
              storageAddress &&
              storageAddress.latitude &&
              storageAddress.longitude
                ? {
                    lat: storageAddress.latitude,
                    lng: storageAddress.longitude,
                    address: addressGenerate(
                      storageAddress?.addressLine1 || "",
                      storageAddress?.addressLine2 || "",
                      storageAddress?.suburb || "",
                      storageAddress?.state || "",
                      storageAddress?.postcode || "",
                      storageAddress?.country || ""
                    ),
                    components: {
                      addressLine1: storageAddress.addressLine1 || "",
                      addressLine2: storageAddress.addressLine2 || "",
                      suburb: storageAddress.suburb || "",
                      state: storageAddress.state || "",
                      postcode: storageAddress.postcode || "",
                      country: storageAddress.country || "",
                    },
                  }
                : undefined
            }
            onConfirm={handleConfirmAddress}
          />
        </>
      ) : (
        isLoading && <Icon.Loading className="ml-2" />
      )}
    </div>
  );
};
